import { cn } from '@utils/cn';
import React from 'react';
import { InvoiceStatus } from '@models/invoice.model';
import { useTranslation } from '@hooks/useTranslation';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'soft';
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs h-5',
  md: 'px-3 py-1 text-sm h-6',
  lg: 'px-4 py-1.5 text-base h-8',
};

const solidByStatus: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'bg-slate-500 text-white',
  [InvoiceStatus.SENDING]: 'bg-blue-500 text-white',
  [InvoiceStatus.SENT]: 'bg-indigo-500 text-white',
  [InvoiceStatus.PAID]: 'bg-emerald-500 text-white',
  [InvoiceStatus.VOIDED]: 'bg-gray-400 text-white',
  [InvoiceStatus.SEND_FAILED]: 'bg-red-500 text-white',
};

const softByStatus: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'bg-slate-100 text-slate-800',
  [InvoiceStatus.SENDING]: 'bg-blue-100 text-blue-800',
  [InvoiceStatus.SENT]: 'bg-indigo-100 text-indigo-800',
  [InvoiceStatus.PAID]: 'bg-emerald-100 text-emerald-800',
  [InvoiceStatus.VOIDED]: 'bg-gray-100 text-gray-800',
  [InvoiceStatus.SEND_FAILED]: 'bg-red-100 text-red-800',
};

export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  size = 'sm',
  variant = 'soft',
  className = '',
}) => {
  // Sprint 29, story-148. Exhaustive map, fail-closed to `common.unknown` — replaces the
  // `INVOICE_STATUS_LABELS[status] || status` leak (raw enum rendered for anything the model's
  // hardcoded-English table didn't cover).
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');
  const invoiceStatusLabels: Record<InvoiceStatus, string> = {
    [InvoiceStatus.DRAFT]: tEnumLabels.invoiceStatusDraft,
    [InvoiceStatus.SENDING]: tEnumLabels.invoiceStatusSending,
    [InvoiceStatus.SENT]: tEnumLabels.invoiceStatusSent,
    [InvoiceStatus.PAID]: tEnumLabels.invoiceStatusPaid,
    [InvoiceStatus.VOIDED]: tEnumLabels.invoiceStatusVoided,
    [InvoiceStatus.SEND_FAILED]: tEnumLabels.invoiceStatusSendFailed,
  };

  const colorMap = variant === 'soft' ? softByStatus : solidByStatus;
  const colorClass = colorMap[status] || 'bg-gray-500 text-white';
  const baseClasses =
    'inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap w-fit';

  return (
    <span className={cn(baseClasses, sizeClasses[size], colorClass, className)}>
      {invoiceStatusLabels[status] || tCommon.unknown}
    </span>
  );
};
